import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Tags, Loader2, Plus, Search, Trash2, Pencil, Check, X, FlaskConical } from "lucide-react";
import { api, errText } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const split = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-");

const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-1">
    {items.map((t) => <span key={t} className="px-2 py-0.5 rounded-full bg-[#FFF4F0] text-[#C2410C] text-xs font-medium">{t}</span>)}
  </div>
);

const Row = ({ row, onSave, onDelete }) => {
  const [edit, setEdit] = useState(false);
  const [nick, setNick] = useState(row.nickname);
  const [targets, setTargets] = useState(row.targets.join(", "));
  const id = slug(row.nickname);
  const reset = () => { setNick(row.nickname); setTargets(row.targets.join(", ")); };
  useEffect(reset, [row]);
  const save = async () => { if (await onSave(row.id, nick, targets)) setEdit(false); };
  if (edit) {
    return (
      <div className="grid grid-cols-[180px_1fr_auto] gap-2 items-center py-2 border-b border-slate-100" data-testid={`syn-row-${id}`}>
        <Input value={nick} onChange={(e) => setNick(e.target.value)} className="h-9 rounded-lg" data-testid={`syn-edit-nick-${id}`} />
        <Input value={targets} onChange={(e) => setTargets(e.target.value)} className="h-9 rounded-lg" data-testid={`syn-edit-targets-${id}`} />
        <div className="flex gap-1">
          <Button size="sm" onClick={save} className="h-9 rounded-lg bg-[#12234A]" data-testid={`syn-save-${id}`}><Check size={14} /></Button>
          <Button size="sm" variant="outline" onClick={() => { reset(); setEdit(false); }} className="h-9 rounded-lg" data-testid={`syn-cancel-${id}`}><X size={14} /></Button>
        </div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-[180px_1fr_auto] gap-2 items-center py-2.5 border-b border-slate-100 group" data-testid={`syn-row-${id}`}>
      <p className="text-sm font-semibold text-[#12234A] truncate">{row.nickname}</p>
      <Chips items={row.targets} />
      <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
        <Button size="sm" variant="outline" onClick={() => setEdit(true)} className="h-8 rounded-lg" data-testid={`syn-edit-${id}`}><Pencil size={13} /></Button>
        <Button size="sm" variant="outline" onClick={() => onDelete(row)} className="h-8 rounded-lg hover:text-red-600" data-testid={`syn-delete-${id}`}><Trash2 size={13} /></Button>
      </div>
    </div>
  );
};

const TryIt = () => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const run = () => q.trim() && api.get(`/staff/synonyms/preview?q=${encodeURIComponent(q)}`).then((r) => setRes(r.data.units)).catch((e) => toast.error(errText(e)));
  return (
    <div className="mt-5 p-4 rounded-xl border border-dashed border-slate-200" data-testid="syn-try">
      <p className="text-sm font-semibold text-[#12234A] flex items-center gap-1.5 mb-2"><FlaskConical size={15} className="text-[#F5551E]" /> Try a search</p>
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="e.g. labs for sale" className="h-9 rounded-lg" data-testid="syn-try-input" />
        <Button size="sm" onClick={run} className="h-9 rounded-lg bg-[#12234A]" data-testid="syn-try-btn">Check</Button>
      </div>
      {res && (
        <ul className="mt-3 space-y-1.5 text-sm" data-testid="syn-try-result">
          {res.map((u) => (
            <li key={u.words} className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-[#12234A]">“{u.words}”</span>
              {u.also.length ? <><span className="text-slate-400">also finds</span><Chips items={u.also} /></> : <span className="text-slate-400">matched as typed (with typo help)</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export function SearchNicknames() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [nick, setNick] = useState("");
  const [targets, setTargets] = useState("");
  const load = () => api.get("/staff/synonyms").then((r) => setRows(r.data.items)).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, []);

  const save = async (id, n, t) => {
    try {
      const body = { nickname: n, targets: split(t) };
      const { data } = id ? await api.put(`/staff/synonyms/${id}`, body) : await api.post("/staff/synonyms", body);
      toast.success(`Saved “${data.nickname}” → ${data.targets.join(", ")}`);
      load();
      return true;
    } catch (e) { toast.error(errText(e)); return false; }
  };
  const [pending, setPending] = useState(null);
  const remove = async () => {
    const row = pending;
    setPending(null);
    try { await api.delete(`/staff/synonyms/${row.id}`); toast.success(`Removed “${row.nickname}”`); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const add = async () => { if (await save(null, nick, targets)) { setNick(""); setTargets(""); } };
  const shown = useMemo(() => (rows || []).filter((r) => !q || `${r.nickname} ${r.targets.join(" ")}`.includes(q.toLowerCase())), [rows, q]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 p-6 max-w-3xl" data-testid="search-nicknames">
      <div className="flex items-start gap-3 mb-4">
        <span className="h-10 w-10 rounded-xl bg-[#FFF4F0] flex items-center justify-center shrink-0"><Tags size={20} className="text-[#F5551E]" /></span>
        <div>
          <h2 className="font-display font-bold text-lg text-[#12234A]">Breed nicknames</h2>
          <p className="text-sm text-slate-500">When someone searches a nickname, listings with any of its breeds show up too, and the other way round. Plurals ("labs") are handled automatically. Changes apply to search within a minute.</p>
        </div>
      </div>
      <div className="mb-4 p-3 rounded-xl bg-slate-50 grid grid-cols-[180px_1fr_auto] gap-2" data-testid="syn-add">
        <Input value={nick} onChange={(e) => setNick(e.target.value)} placeholder="Nickname (e.g. GSD)" className="h-10 rounded-lg bg-white" data-testid="syn-add-nick" />
        <Input value={targets} onChange={(e) => setTargets(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Also finds, comma separated (e.g. German Shepherd)" className="h-10 rounded-lg bg-white" data-testid="syn-add-targets" />
        <Button onClick={add} disabled={!nick.trim() || !split(targets).length} className="h-10 rounded-lg bg-[#F5551E] hover:bg-[#e04812] gap-1" data-testid="syn-add-btn"><Plus size={15} /> Add</Button>
      </div>
      <div className="relative mb-2">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter nicknames…" className="h-10 rounded-lg pl-9" data-testid="syn-filter" />
      </div>
      {!rows ? <Loader2 className="animate-spin text-slate-400 my-6" /> : (
        <div className="max-h-[480px] overflow-y-auto pr-1" data-testid="syn-list">
          <p className="text-xs text-slate-400 mb-1" data-testid="syn-count">{rows.length} nicknames</p>
          {shown.map((r) => <Row key={r.id} row={r} onSave={save} onDelete={setPending} />)}
          {!shown.length && <p className="text-sm text-slate-400 py-6 text-center">No matching nicknames</p>}
        </div>
      )}
      <TryIt />
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent data-testid="syn-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove “{pending?.nickname}”?</AlertDialogTitle>
            <AlertDialogDescription>Searches for this nickname will stop finding {pending?.targets.join(", ")}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="syn-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="syn-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
